import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTelegram } from '../contexts/TelegramContext';
import { guestAPI, authAPI } from '../services/api';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import ReCAPTCHA from 'react-google-recaptcha';
import { SeoHead } from '../components/SeoHead';
import { getSeoForPath } from '../config/seoPages';

// 主题切换按钮组件
const ThemeToggleButton: React.FC = () => {
  const { toggleTheme, resolvedTheme } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors shadow-sm"
      title="切换主题"
    >
      {resolvedTheme === 'dark' ? (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ) : (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      )}
    </button>
  );
};

interface SiteConfig {
  is_email_verify: number;
  is_invite_force: number;
  email_whitelist_suffix: number | string[];
  is_recaptcha: number;
  recaptcha_site_key: string;
  tos_url: string;
  app_description: string;
  app_url: string;
}

export const Register: React.FC = () => {
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('code');
  const [formData, setFormData] = useState({
    email: '',
    emailUsername: '',
    emailSuffix: '',
    password: '',
    confirmPassword: '',
    email_code: '',
    invite_code: inviteCode || '',
    recaptcha_data: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [siteConfig, setSiteConfig] = useState<SiteConfig | null>(null);
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [emailCodeLoading, setEmailCodeLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const { register, user, loading: authLoading } = useAuth();
  const { isInTelegram, user: telegramUser, initData } = useTelegram();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const seoMeta = getSeoForPath(pathname);

  // 获取站点配置
  useEffect(() => {
    const fetchSiteConfig = async () => {
      try {
        const response = await guestAPI.commonConfig();
        setSiteConfig(response.data.data);
        
        // 如果有邮箱后缀白名单，设置默认后缀
        if (response.data.data?.email_whitelist_suffix && Array.isArray(response.data.data.email_whitelist_suffix) && response.data.data.email_whitelist_suffix.length > 0) {
          setFormData(prev => ({
            ...prev,
            emailSuffix: response.data.data.email_whitelist_suffix[0]
          }));
        }
      } catch (error) {
        console.error('Failed to fetch site config:', error);
      }
    };
    fetchSiteConfig();
  }, []);

  // 检查用户是否已登录，如果已登录则自动跳转到dashboard
  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // 倒计时逻辑
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);




  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // 处理邮箱用户名变化
  const handleEmailUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const username = e.target.value;
    const suffixList = (siteConfig && Array.isArray(siteConfig.email_whitelist_suffix)) ? siteConfig.email_whitelist_suffix : [];
    const suffix = formData.emailSuffix || (suffixList[0] || '');
    setFormData({
      ...formData,
      emailUsername: username,
      email: username ? username + '@' + suffix.replace(/^@/, '') : '',
    });
  };

  // 处理邮箱后缀变化
  const handleEmailSuffixChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const suffix = e.target.value;
    const username = formData.emailUsername || '';
    setFormData({
      ...formData,
      emailSuffix: suffix,
      email: username ? username + '@' + suffix.replace(/^@/, '') : '',
    });
  };

  // reCAPTCHA回调
  const onRecaptchaChange = (value: string | null) => {
    setFormData({
      ...formData,
      recaptcha_data: value || '',
    });
    if (value) {
      setError(''); // 清除错误信息
    }
  };

  // 发送邮箱验证码
  const sendEmailCode = async () => {
    if (!formData.email) {
      setError('请先输入邮箱地址');
      return;
    }

    // reCAPTCHA验证（如果开启）
    if (siteConfig?.is_recaptcha === 1) {
      const recaptchaValue = recaptchaRef.current?.getValue();
      if (!recaptchaValue) {
        setError('请先完成人机验证');
        return;
      }
    }

    // 检查邮箱后缀白名单
    if (siteConfig?.email_whitelist_suffix && Array.isArray(siteConfig.email_whitelist_suffix)) {
      const emailDomain = formData.email.split('@')[1];
      if (!siteConfig.email_whitelist_suffix.includes(emailDomain)) {
        setError(`邮箱后缀不在白名单中，仅支持：${siteConfig.email_whitelist_suffix.join(', ')}`);
        return;
      }
    }

    setEmailCodeLoading(true);
    setError('');

    try {
      await authAPI.sendEmailVerify(formData.email);
      setEmailCodeSent(true);
      setCountdown(60); // 60秒倒计时
    } catch (err: any) {
      setError(err.response?.data?.message || '发送验证码失败，请稍后重试');
    } finally {
      setEmailCodeLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // 基础验证
    if (formData.password !== formData.confirmPassword) {
      setError('两次输入的密码不一致');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('密码长度至少为8位');
      setLoading(false);
      return;
    }

    // 邮箱验证码验证
    if (siteConfig?.is_email_verify && !formData.email_code) {
      setError('请输入邮箱验证码');
      setLoading(false);
      return;
    }

    // 邀请码验证
    if (siteConfig?.is_invite_force && !formData.invite_code && !inviteCode) {
      setError('请输入邀请码');
      setLoading(false);
      return;
    }

    // 邮箱后缀白名单验证
    if (siteConfig?.email_whitelist_suffix && Array.isArray(siteConfig.email_whitelist_suffix)) {
      const emailDomain = formData.email.split('@')[1];
      if (!siteConfig.email_whitelist_suffix.includes(emailDomain)) {
        setError(`邮箱后缀不在白名单中，仅支持：${siteConfig.email_whitelist_suffix.join(', ')}`);
        setLoading(false);
        return;
      }
    }

    // reCAPTCHA验证
    if (siteConfig?.is_recaptcha === 1) {
      const recaptchaValue = recaptchaRef.current?.getValue();
      if (!recaptchaValue) {
        setError('请完成人机验证');
        setLoading(false);
        return;
      }
      // 更新formData中的recaptcha_data
      formData.recaptcha_data = recaptchaValue;
    }

    try {
      await register(
        formData.email,
        formData.password,
        formData.email_code || undefined,
        formData.invite_code || inviteCode || undefined,
        formData.recaptcha_data || undefined,
        isInTelegram && telegramUser ? telegramUser.id : undefined,
        isInTelegram && initData ? initData : undefined
      );
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || '注册失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SeoHead meta={seoMeta} />
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8 transition-colors">
      {/* 主题切换按钮 */}
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggleButton />
      </div>
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900 dark:text-white transition-colors">
            创建新账户
          </h2>
          {isInTelegram && telegramUser && (
            <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-700 dark:text-green-300">
                    欢迎通过 Telegram 注册，{telegramUser.first_name}！
                    <br />
                    注册后您的 Telegram 账户将自动关联，方便下次快速登录。
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    使用虚假邮箱注册可能会导致账号被封禁，请注意。
                  </p>
                </div>
              </div>
            </div>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400 transition-colors">
            或者{' '}
            <Link
              to="/login"
              className="font-medium text-primary-600 hover:text-primary-500 transition-colors"
            >
              登录已有账户
            </Link>
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                邮箱地址
              </label>
              {siteConfig?.email_whitelist_suffix && Array.isArray(siteConfig.email_whitelist_suffix) && siteConfig.email_whitelist_suffix.length > 0 ? (
                <div className="mt-1 flex">
                  <input
                    id="emailUsername"
                    name="emailUsername"
                    type="text"
                    autoComplete="email"
                    required
                    value={formData.emailUsername}
                    onChange={handleEmailUsernameChange}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 transition-colors dark:bg-gray-700 dark:text-white"
                    placeholder="请输入邮箱用户名"
                  />
                  <select
                    name="emailSuffix"
                    value={formData.emailSuffix || siteConfig.email_whitelist_suffix[0]}
                    onChange={handleEmailSuffixChange}
                    className="px-3 py-2 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 transition-colors dark:bg-gray-700 dark:text-white bg-gray-50 dark:bg-gray-600"
                  >
                    {siteConfig.email_whitelist_suffix.map((suffix) => (
                      <option key={suffix} value={suffix}>
                        {suffix}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 transition-colors dark:bg-gray-700 dark:text-white"
                  placeholder="请输入邮箱地址"
                />
              )}
            </div>
            
            {/* 邮箱验证码 */}
            {siteConfig?.is_email_verify === 1 && (
              <div>
                <label htmlFor="email_code" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  邮箱验证码 *
                </label>
                <div className="mt-1 flex space-x-2">
                  <input
                    id="email_code"
                    name="email_code"
                    type="text"
                    required
                    value={formData.email_code}
                    onChange={handleChange}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 transition-colors dark:bg-gray-700 dark:text-white"
                    placeholder="请输入邮箱验证码"
                  />
                  <button
                    type="button"
                    onClick={sendEmailCode}
                    disabled={emailCodeLoading || countdown > 0 || !formData.email}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-md transition-colors"
                  >
                    {emailCodeLoading ? '发送中...' : countdown > 0 ? `${countdown}s` : emailCodeSent ? '重新发送' : '发送验证码'}
                  </button>
                </div>
                {emailCodeSent && (
                  <p className="mt-1 text-sm text-green-600 dark:text-green-400">
                    验证码已发送到您的邮箱，请查收
                  </p>
                )}
              </div>
            )}
            
            {/* 邀请码 */}
            {(siteConfig?.is_invite_force === 1 || inviteCode) && (
              <div>
                <label htmlFor="invite_code" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  邀请码 {siteConfig?.is_invite_force === 1 ? '*' : ''}
                </label>
                <input
                  id="invite_code"
                  name="invite_code"
                  type="text"
                  required={siteConfig?.is_invite_force === 1}
                  value={formData.invite_code || inviteCode || ''}
                  onChange={handleChange}
                  disabled={!!inviteCode}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 transition-colors dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                  placeholder={inviteCode ? `已自动填入邀请码: ${inviteCode}` : "请输入邀请码"}
                />
              </div>
            )}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                密码
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 transition-colors dark:bg-gray-700 dark:text-white"
                  placeholder="请输入密码（至少8位）"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                确认密码
              </label>
              <div className="mt-1 relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 transition-colors dark:bg-gray-700 dark:text-white"
                  placeholder="请再次输入密码"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
            
            {/* reCAPTCHA */}
            {siteConfig?.is_recaptcha === 1 && siteConfig.recaptcha_site_key && (
              <div className="flex justify-center">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={siteConfig.recaptcha_site_key}
                  onChange={onRecaptchaChange}
                  theme="light"
                  hl="zh-CN"
                  size="normal"
                  tabindex={0}
                />
              </div>
            )}
            
            {/* 提示信息：如果reCAPTCHA加载失败 */}
            {/* {siteConfig?.is_recaptcha === 1 && siteConfig.recaptcha_site_key && (
              <div className="text-xs text-gray-500 text-center mt-2">
                如果人机验证无法加载，请尝试切换网络环境后重试或联系管理员
              </div>
            )} */}
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? '注册中...' : '注册'}
            </button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};